import { useSystemsList } from '@/api/hooks/useCrossroads'
import {
  NotificationConfig,
  SystemInstance,
  SystemInstanceUpsert,
  TestCredentialsResult,
  useDeleteInstance,
  useTenantConfig,
  useTestInstanceCredentials,
  useUpsertInstance,
} from '@/api/hooks/useTenantConfig'
import { SystemType } from '@/api/types'
import { HeaderWithTooltip } from '@/components/Grid/HeaderWithTooltip'
import { useSecurityContext } from '@/contexts/SecurityContext'
import {
  ApiOutlined,
  DeleteOutlined,
  PlusOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons'
import { GraviGrid, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import {
  Button,
  Checkbox,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tooltip,
  Typography,
  message,
  theme,
} from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CrossroadsIntegrationModal } from './CrossroadsIntegrationModal'
import { LearnMoreAlert } from './LearnMoreAlert'
import { NotificationConfigModal } from './NotificationConfigModal'
import { TestCredentialsModal } from './TestCredentialsModal'

export function InstancesGrid() {
  const { token } = theme.useToken()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingInstance, setEditingInstance] = useState<SystemInstance | null>(
    null,
  )
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false)
  const [notificationInstance, setNotificationInstance] =
    useState<SystemInstance | null>(null)
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false)
  const [integrationInstance, setIntegrationInstance] =
    useState<SystemInstance | null>(null)
  const [isTestModalOpen, setIsTestModalOpen] = useState(false)
  const [testingInstance, setTestingInstance] = useState<SystemInstance | null>(
    null,
  )
  const [testResult, setTestResult] = useState<TestCredentialsResult | null>(
    null,
  )
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false)
  const [form] = Form.useForm()
  const selectedSystemId = Form.useWatch('system_id', form)

  const { hasGravitateAccess } = useSecurityContext()
  const isGravitateUser = hasGravitateAccess()
  const { data: config, isLoading, refetch } = useTenantConfig()
  const upsertMutation = useUpsertInstance()
  const deleteMutation = useDeleteInstance()
  const testCredentialsMutation = useTestInstanceCredentials()

  const { data: externalSystems } = useSystemsList()

  const instances = config?.instances || []

  // Build system options for dropdown
  const systemOptions = useMemo(() => {
    if (!externalSystems) return []
    return externalSystems.map((system) => ({
      value: system._id,
      label: system.name,
      description: system.description,
    }))
  }, [externalSystems])

  /** Whether an instance's system is Gravitate S&D, resolved through the
   * systems list by id. The instance's own `system_name` is the system's free
   * text name and is only written when the instance is first created, so it is
   * blank on older instances and stale if the system is changed -- reading it
   * would hide the S&D-only fields from real S&D instances. */
  const isSupplyAndDispatch = useCallback(
    (instance: SystemInstance | null) => {
      if (!instance || !externalSystems) return false
      const system = externalSystems.find((s) => s._id === instance.system_id)
      return system?.type === SystemType.SND
    },
    [externalSystems],
  )

  // Check if selected system is "External System" type (credentials not needed)
  const isExternalSystem = useMemo(() => {
    if (!selectedSystemId || !externalSystems) return false
    const selectedSystem = externalSystems.find(
      (s) => s._id === selectedSystemId,
    )
    return selectedSystem?.type === 'External System'
  }, [selectedSystemId, externalSystems])

  // Clear credential fields when switching to external system
  useEffect(() => {
    if (isExternalSystem) {
      form.setFieldsValue({
        url: undefined,
        client_id: undefined,
        client_secret: undefined,
      })
    }
  }, [isExternalSystem, form])

  const primaryInstance = useMemo(
    () => config?.instances.find((inst) => inst.primary),
    [config?.instances],
  )

  const handleSetPrimary = (instance: SystemInstance) => {
    if (instance.primary) return // Already primary

    upsertMutation.mutate(
      { id: instance.id, primary: true },
      {
        onSuccess: () => {
          message.success(`${instance.system_id} is now the primary instance`)
          refetch()
        },
        onError: (error) => {
          message.error(`Failed to set primary: ${error.message}`)
        },
      },
    )
  }

  const columnDefs: ColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      editable: true,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip: 'Unique, human-friendly name for this instance',
      },
    },
    {
      field: 'system_name',
      headerName: 'System',
      flex: 1,
      minWidth: 150,
      editable: false,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip: 'The external system type this instance connects to',
      },
      cellRenderer: (params: ICellRendererParams) => params.data?.system_name,
    },
    {
      field: 'url',
      headerName: 'Base URL',
      flex: 2,
      minWidth: 250,
      editable: true,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip: 'The URL used to connect to this instance',
      },
    },
    {
      field: 'client_id',
      headerName: 'Client ID',
      flex: 1,
      minWidth: 150,
      editable: true,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip: 'The API key or client identifier for this instance',
      },
      cellRenderer: (params: ICellRendererParams) => {
        const value = params.value || ''
        if (value.length <= 6) return value
        return (
          <span style={{ color: token.colorTextSecondary }}>
            ••••••{value.slice(-6)}
          </span>
        )
      },
    },
    {
      field: 'client_secret',
      headerName: 'Client Secret',
      width: 140,
      editable: true,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip: 'The secret key paired with the Client ID',
      },
      cellRenderer: () => (
        <span style={{ color: token.colorTextTertiary }}>••••••••</span>
      ),
    },
    ...(isGravitateUser
      ? [
          {
            field: 'crossroads_integration',
            headerName: 'Crossroads Integration',
            width: 180,
            editable: false,
            headerComponent: HeaderWithTooltip,
            headerComponentParams: {
              tooltip:
                'Configure how Crossroads exchanges messages with this instance',
            },
            cellRenderer: (params: ICellRendererParams) => {
              return (
                <div
                  style={{
                    display: 'flex',
                    gap: 4,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    size='small'
                    type='default'
                    onClick={() => {
                      setIntegrationInstance(params.data)
                      setIsIntegrationModalOpen(true)
                    }}
                  >
                    Configure
                  </Button>
                </div>
              )
            },
          } satisfies ColDef,
        ]
      : []),
    {
      field: 'notification_config',
      headerName: 'Notifications',
      width: 120,
      editable: false,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div
            style={{
              display: 'flex',
              gap: 4,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Button
              size='small'
              type='default'
              onClick={() => {
                setNotificationInstance(params.data)
                setIsNotificationModalOpen(true)
              }}
            >
              Configure
            </Button>
          </div>
        )
      },
    },
    {
      field: 'primary',
      headerName: 'Primary',
      width: 100,
      editable: false,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip:
          'The default instance. Crossroads will sync mappings from this instance',
      },
      cellRenderer: (params: ICellRendererParams) => (
        <Tooltip
          title={params.value ? 'Primary instance' : 'Click to set as primary'}
        >
          <Popconfirm
            title='Set as Primary Instance?'
            description={`This will replace ${primaryInstance?.system_id} primary instance.`}
            onConfirm={() => handleSetPrimary(params.data)}
            placement='bottom'
          >
            <Button
              type='text'
              icon={
                params.value ? (
                  <StarFilled style={{ color: token.colorWarning }} />
                ) : (
                  <StarOutlined style={{ color: token.colorTextTertiary }} />
                )
              }
            />
          </Popconfirm>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      editable: false,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams) => {
        const instance = params.data as SystemInstance
        const hasCredentials = instance.url && instance.client_id

        return (
          <Space size='small'>
            {hasCredentials && (
              <Tooltip title='Test credentials'>
                <Button
                  icon={<ApiOutlined />}
                  size='small'
                  onClick={() => handleTestCredentials(instance)}
                />
              </Tooltip>
            )}
            <Tooltip
              title={
                instance.primary
                  ? 'Cannot delete primary instance'
                  : 'Delete instance'
              }
            >
              <Popconfirm
                title='Delete Instance'
                description={`Are you sure you want to delete ${instance.system_id}?`}
                onConfirm={() => handleDelete(instance)}
                okText='Delete'
                okButtonProps={{ danger: true }}
                disabled={instance.primary}
              >
                <Button
                  icon={<DeleteOutlined />}
                  size='small'
                  danger
                  disabled={instance.primary}
                />
              </Popconfirm>
            </Tooltip>
          </Space>
        )
      },
    },
  ]

  const handleDelete = (instance: SystemInstance) => {
    deleteMutation.mutate(instance.id, {
      onSuccess: () => {
        message.success('Instance deleted successfully')
        refetch()
      },
      onError: (error) => {
        message.error(`Failed to delete instance: ${error.message}`)
      },
    })
  }

  const handleNotificationSave = (config: NotificationConfig) => {
    if (!notificationInstance) return
    upsertMutation.mutate(
      { id: notificationInstance.id, notification_config: config },
      {
        onSuccess: () => {
          message.success('Notification settings updated')
          setIsNotificationModalOpen(false)
          setNotificationInstance(null)
          refetch()
        },
        onError: (error) => {
          message.error(`Failed to update: ${error.message}`)
        },
      },
    )
  }

  const handleTestCredentials = (instance: SystemInstance) => {
    setTestingInstance(instance)
    setTestResult(null)
    setIsTestModalOpen(true)

    testCredentialsMutation.mutate(instance.id, {
      onSuccess: (result) => {
        setTestResult(result)
      },
      onError: (error) => {
        setTestResult({
          success: false,
          error_message: error.message,
          error_category: 'connection',
          scopes: [],
        })
      },
    })
  }

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      // Look up the system name from the selected system_id
      const selectedSystem = externalSystems?.find(
        (s) => s._id === values.system_id,
      )
      const instanceData: Partial<SystemInstance> = editingInstance
        ? {
            id: editingInstance.id,
            ...values,
            system_name: selectedSystem?.name,
          }
        : { ...values, system_name: selectedSystem?.name }

      upsertMutation.mutate(instanceData, {
        onSuccess: () => {
          message.success(
            editingInstance
              ? 'Instance updated successfully'
              : 'Instance created successfully',
          )
          setIsModalOpen(false)
          setEditingInstance(null)
          form.resetFields()
          refetch()
        },
        onError: (error) => {
          message.error(`Failed to save instance: ${error.message}`)
        },
      })
    })
  }

  return (
    <Vertical bg='layout' style={{ height: '100%', width: '100%' }}>
      <LearnMoreAlert
        message='Configure your connected system instances and how Crossroads manages them.'
        onLearnMore={() => setIsLearnMoreOpen(true)}
      />
      <GraviGrid
        updateEP={async (values, cellChangeMeta) => {
          const updateData: Partial<SystemInstanceUpsert> = {
            id: values.id,
            ...(cellChangeMeta?.colId && {
              [cellChangeMeta.colId]: cellChangeMeta.newValue,
            }),
          }
          upsertMutation.mutate(updateData, {
            onSuccess: () => {
              message.success('Instance updated successfully')
              refetch()
            },
          })
        }}
        rowData={instances}
        columnDefs={columnDefs}
        loading={isLoading}
        controlBarProps={{
          title: 'System Instances',
          actionButtons: (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingInstance(null)
                form.resetFields()
                setIsModalOpen(true)
              }}
            >
              Add Instance
            </Button>
          ),
        }}
        agPropOverrides={{
          getRowId: (params) => {
            const id = params.data.id
            // Handle both string IDs and MongoDB ObjectId objects
            if (typeof id === 'string') return id
            if (id?.$oid) return id.$oid
            return String(id)
          },
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
          },
          animateRows: true,
          singleClickEdit: true,
          stopEditingWhenCellsLoseFocus: true,
        }}
      />

      <Modal
        title={editingInstance ? 'Edit Instance' : 'Add Instance'}
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalOpen(false)
          setEditingInstance(null)
          form.resetFields()
        }}
        confirmLoading={upsertMutation.isPending}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='name'
            label='Instance Name'
            rules={[
              {
                required: !editingInstance,
                message: 'Please enter an instance name',
              },
            ]}
          >
            <Input placeholder='e.g., Production S&D' />
          </Form.Item>
          <Form.Item
            name='system_id'
            label='External System'
            rules={[
              {
                required: !editingInstance,
                message: 'Please select an external system',
              },
            ]}
          >
            <Select
              placeholder='Select an external system'
              showSearch
              optionFilterProp='label'
              options={systemOptions}
              optionRender={(option) => (
                <div>
                  <div>{option.data.label}</div>
                  {option.data.description && (
                    <div
                      style={{ fontSize: 12, color: token.colorTextSecondary }}
                    >
                      {option.data.description}
                    </div>
                  )}
                </div>
              )}
            />
          </Form.Item>

          <Form.Item
            name='url'
            label='Base URL'
            rules={[
              {
                required: !editingInstance && !isExternalSystem,
                message: 'Please enter a base URL',
              },
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <Input
              placeholder='https://api.example.com'
              disabled={isExternalSystem}
            />
          </Form.Item>

          <Form.Item
            name='client_id'
            label='Client ID'
            rules={[
              {
                required: !editingInstance && !isExternalSystem,
                message: 'Please enter a client ID',
              },
            ]}
          >
            <Input
              placeholder='Client ID for authentication'
              disabled={isExternalSystem}
            />
          </Form.Item>

          <Form.Item
            name='client_secret'
            label='Client Secret'
            rules={[
              {
                required: !editingInstance && !isExternalSystem,
                message: 'Please enter a client secret',
              },
            ]}
            extra={
              editingInstance
                ? 'Leave blank to keep existing secret'
                : undefined
            }
          >
            <Input.Password
              placeholder={
                editingInstance ? 'Enter new secret to update' : 'Client secret'
              }
              disabled={isExternalSystem}
            />
          </Form.Item>

          {!editingInstance && instances.length === 0 && (
            <Form.Item
              name='primary'
              valuePropName='checked'
              initialValue={true}
            >
              <Checkbox disabled>
                Primary instance (first instance is always primary)
              </Checkbox>
            </Form.Item>
          )}

          {!editingInstance && instances.length > 0 && (
            <Form.Item name='primary' valuePropName='checked'>
              <Checkbox>Set as primary instance</Checkbox>
            </Form.Item>
          )}
        </Form>
      </Modal>

      <NotificationConfigModal
        open={isNotificationModalOpen}
        onClose={() => {
          setIsNotificationModalOpen(false)
          setNotificationInstance(null)
        }}
        instance={notificationInstance}
        onSave={handleNotificationSave}
        loading={upsertMutation.isPending}
      />

      <CrossroadsIntegrationModal
        open={isIntegrationModalOpen}
        onClose={() => {
          setIsIntegrationModalOpen(false)
          setIntegrationInstance(null)
        }}
        instance={integrationInstance}
        isSupplyAndDispatch={isSupplyAndDispatch(integrationInstance)}
        onSave={(values) => {
          if (!integrationInstance) return
          upsertMutation.mutate(
            { id: integrationInstance.id, ...values },
            {
              onSuccess: () => {
                message.success('Crossroads integration updated')
                setIsIntegrationModalOpen(false)
                setIntegrationInstance(null)
                refetch()
              },
              onError: (error) =>
                message.error(`Failed to update: ${error.message}`),
            },
          )
        }}
        loading={upsertMutation.isPending}
      />

      <TestCredentialsModal
        open={isTestModalOpen}
        onClose={() => {
          setIsTestModalOpen(false)
          setTestingInstance(null)
          setTestResult(null)
        }}
        result={testResult}
        loading={testCredentialsMutation.isPending}
        instanceName={testingInstance?.name}
      />

      <Drawer
        title='About Instances'
        open={isLearnMoreOpen}
        onClose={() => setIsLearnMoreOpen(false)}
      >
        <Typography.Paragraph>
          Instances are specific environments you are integrating with
          Crossroads. On this screen you can control what instances you have
          available and how Crossroads should manage them.
        </Typography.Paragraph>
      </Drawer>
    </Vertical>
  )
}
